import React, { useEffect, useMemo, useState } from 'react';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  fetchMonitoringAlerts,
  fetchMonitoringImageQueue,
  fetchMonitoringOverview,
  fetchMonitoringServices,
  acknowledgeMonitoringAlert,
  resolveMonitoringAlert,
} from '../../api/adminMonitoring';

const STATUS_ACCENT = {
  GREEN: '#8fd6b1',
  ORANGE: '#f3c65c',
  RED: '#d96c6c',
};

const SEVERITY_ORDER = { GREEN: 0, ORANGE: 1, RED: 2 };

function StatBadge({ label, value, accent = '#19323c' }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(247,243,234,0.12)',
        minWidth: 132,
      }}
    >
      <Typography variant="caption" sx={{ color: 'rgba(247,243,234,0.72)', display: 'block', mb: 0.35 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: accent, fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('fr-FR');
}

function formatAgo(value) {
  if (!value) {
    return 'n/a';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'n/a';
  }
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  if (seconds < 3600) {
    return `${Math.round(seconds / 60)} min ago`;
  }
  return `${Math.round(seconds / 3600)} h ago`;
}

function formatDuration(ms) {
  const value = Number(ms);
  if (!Number.isFinite(value) || value < 0) {
    return 'n/a';
  }
  const seconds = Math.floor(value / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)} min`;
  }
  return `${Math.floor(seconds / 3600)} h`;
}

function SeverityIcon({ severity }) {
  if (severity === 'GREEN') {
    return <CheckCircleRoundedIcon sx={{ color: STATUS_ACCENT.GREEN }} />;
  }
  if (severity === 'RED') {
    return <ErrorOutlineRoundedIcon sx={{ color: STATUS_ACCENT.RED }} />;
  }
  return <WarningAmberRoundedIcon sx={{ color: STATUS_ACCENT.ORANGE }} />;
}

function ServiceCard({ service }) {
  const accent = STATUS_ACCENT[service?.severity] || '#19323c';
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        background: '#fcf8ef',
        border: `1px solid ${accent}55`,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
            <SeverityIcon severity={service?.severity} />
            <Chip size="small" label={service?.severity} sx={{ bgcolor: accent, color: '#17323c', fontWeight: 800 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c' }}>
              {service?.name || 'Service'}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: '#4f646f' }}>
            {service?.up ? 'UP' : 'DOWN'} · {service?.latencyMs ?? 'n/a'} ms · checked {formatAgo(service?.lastCheckedAt)}
          </Typography>
          {service?.message && (
            <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block' }}>
              {service.message}
            </Typography>
          )}
          {!service?.up && service?.consecutiveFailures > 0 && (
            <Typography variant="caption" sx={{ color: '#a43e2a', display: 'block' }}>
              Echecs consecutifs: {service.consecutiveFailures} · dernier succes {formatAgo(service?.lastSuccessAt)}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function ImageQueueCard({ queue }) {
  const accent = STATUS_ACCENT[queue?.severity] || '#19323c';
  const queuePercent = Math.min(100, Math.max(0, Number(queue?.queuePercent) || 0));
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        background: '#fcf8ef',
        border: `1px solid ${accent}55`,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
            <SeverityIcon severity={queue?.severity} />
            <Chip size="small" label={queue?.severity} sx={{ bgcolor: accent, color: '#17323c', fontWeight: 800 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c' }}>
              Pattern Image Queue
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: '#4f646f' }}>
            Pending {queue?.totalPending ?? 'n/a'} · File {queue?.queueSize ?? 'n/a'}/{queue?.queueCapacity ?? 'n/a'} ({queuePercent}%)
            {queue?.stalled ? ' · bloquee' : ''}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block' }}>
            Actifs {queue?.activeThreads ?? 'n/a'}/{queue?.maxThreads ?? 'n/a'} · Completed {queue?.totalCompleted ?? 'n/a'} · Uptime {formatDuration(queue?.uptimeMs)} · checked {formatAgo(queue?.lastCheckedAt)}
          </Typography>
          {queue?.message && (
            <Typography variant="caption" sx={{ color: '#a43e2a', display: 'block' }}>
              {queue.message}
            </Typography>
          )}
        </Box>

        <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
          <LinearProgress
            variant="determinate"
            value={queuePercent}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: 'rgba(18,44,52,0.08)',
              '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: accent },
            }}
          />
          <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mt: 0.5 }}>
            Saturation de la file
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const MonitoringAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [services, setServices] = useState([]);
  const [imageQueue, setImageQueue] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [resolvingId, setResolvingId] = useState('');
  const [acknowledgingId, setAcknowledgingId] = useState('');

  const load = async () => {
    setError('');
    try {
      const [overviewPayload, servicesPayload, imageQueuePayload, alertsPayload] = await Promise.all([
        fetchMonitoringOverview(),
        fetchMonitoringServices(),
        fetchMonitoringImageQueue(),
        fetchMonitoringAlerts('OPEN'),
      ]);
      setOverview(overviewPayload);
      setServices(servicesPayload || []);
      setImageQueue(imageQueuePayload);
      setAlerts(alertsPayload);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const counts = useMemo(() => {
    const jobs = overview?.jobs || [];
    return jobs.reduce(
      (acc, job) => {
        acc[job.status] = (acc[job.status] || 0) + 1;
        return acc;
      },
      { GREEN: 0, ORANGE: 0, RED: 0 },
    );
  }, [overview]);

  const globalSeverity = useMemo(() => {
    let worst = 'GREEN';
    (overview?.jobs || []).forEach((job) => {
      if (SEVERITY_ORDER[job.status] > SEVERITY_ORDER[worst]) {
        worst = job.status;
      }
    });
    (services || []).forEach((service) => {
      if (SEVERITY_ORDER[service.severity] > SEVERITY_ORDER[worst]) {
        worst = service.severity;
      }
    });
    if (imageQueue?.severity && SEVERITY_ORDER[imageQueue.severity] > SEVERITY_ORDER[worst]) {
      worst = imageQueue.severity;
    }
    if ((alerts || []).some((alert) => alert.level === 'RED')) {
      worst = 'RED';
    }
    return worst;
  }, [overview, services, imageQueue, alerts]);

  const handleAcknowledge = async (id) => {
    setAcknowledgingId(id);
    setError('');
    try {
      await acknowledgeMonitoringAlert(id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setAcknowledgingId('');
    }
  };

  const handleResolve = async (id) => {
    setResolvingId(id);
    setError('');
    try {
      await resolveMonitoringAlert(id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setResolvingId('');
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Monitoring" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Cockpit operationnel
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Schedulers, services externes (Python / CCXT), generation d'images et incidents actifs dans un seul ecran.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <StatBadge label="System Health" value={globalSeverity} accent={STATUS_ACCENT[globalSeverity]} />
              <StatBadge label="Jobs" value={`${counts.GREEN}/${counts.ORANGE}/${counts.RED}`} accent="#f7f3ea" />
              <StatBadge label="Alertes ouvertes" value={overview?.openAlertCount ?? 'n/a'} accent={overview?.openAlertCount > 0 ? '#d96c6c' : '#f7f3ea'} />
              <Button
                variant="outlined"
                onClick={load}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshRoundedIcon />}
                sx={{ minHeight: 48, borderRadius: 99, borderColor: '#f7f3ea', color: '#f7f3ea' }}
              >
                {loading ? 'Lecture...' : 'Rafraichir'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
            Services
          </Typography>
          <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
            Sante des services externes, verifiee periodiquement par le backend (pas d'appel a chaque refresh).
          </Typography>

          {!loading && !error && (services || []).length === 0 && (
            <Typography variant="body2" sx={{ color: '#4f646f' }}>
              Aucun service surveille. Le premier check backend n'a pas encore eu lieu.
            </Typography>
          )}

          <Stack spacing={1.4}>
            {(services || []).map((service) => (
              <ServiceCard key={service.key} service={service} />
            ))}
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
            Image Generation
          </Typography>
          <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
            File de generation des images patterns (ThreadPoolExecutor en memoire).
          </Typography>

          {!loading && !error && !imageQueue && (
            <Typography variant="body2" sx={{ color: '#4f646f' }}>
              Aucun echantillon de queue disponible.
            </Typography>
          )}

          {imageQueue && <ImageQueueCard queue={imageQueue} />}
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
            Scheduled Jobs
          </Typography>
          <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
            {overview ? `Dernier controle: ${formatDateTime(overview.checkedAt)}` : 'Chargement...'}
          </Typography>

          {!loading && !error && (overview?.jobs || []).length === 0 && (
            <Typography variant="body2" sx={{ color: '#4f646f' }}>
              Aucun job surveille.
            </Typography>
          )}

          <Stack spacing={1.4}>
            {(overview?.jobs || []).map((job) => {
              const accent = STATUS_ACCENT[job.status] || '#19323c';
              return (
                <Paper
                  key={job.key}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    background: '#fcf8ef',
                    border: '1px solid rgba(18,44,52,0.08)',
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c', mb: 0.2 }}>
                        {job.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6c7a80' }}>
                        {job.key} · cron {job.cron}
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Chip size="small" label={job.status} sx={{ bgcolor: accent, color: '#17323c', fontWeight: 800 }} />
                      <Typography variant="caption" sx={{ color: '#4f646f' }}>
                        Dernier run: {formatDateTime(job.lastRunAt)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#4f646f' }}>
                        Prochain: {formatDateTime(job.nextExpectedAt)}
                      </Typography>
                    </Stack>
                  </Stack>
                  {job.message && (
                    <Typography variant="body2" sx={{ color: '#4f646f', mt: 0.6 }}>
                      {job.message}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
            Alertes actives
          </Typography>
          <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
            Incidents actifs. Un email RED est envoye une seule fois par incident jusqu'a resolution.
          </Typography>

          {!loading && !error && alerts.length === 0 && (
            <Typography variant="body2" sx={{ color: '#4f646f' }}>
              Aucune alerte ouverte.
            </Typography>
          )}

          <Stack spacing={1.4}>
            {alerts.map((alert) => {
              const accent = alert.level === 'RED' ? '#d96c6c' : '#f3c65c';
              return (
                <Paper
                  key={alert.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    background: '#fcf8ef',
                    border: '1px solid rgba(18,44,52,0.08)',
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
                        <Chip size="small" label={alert.level} sx={{ bgcolor: accent, color: '#17323c', fontWeight: 800 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c' }}>
                          {alert.jobKey} - {alert.title}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        {alert.message}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6c7a80' }}>
                        Depuis {formatDateTime(alert.openedAt)} — Occurrences {alert.occurrenceCount} — Email {alert.emailSent ? 'envoye' : 'non envoye'}
                      </Typography>
                      {alert.acknowledgedAt && (
                        <Typography variant="caption" sx={{ color: '#2f6b50', display: 'block' }}>
                          Acquittee par {alert.acknowledgedBy || 'n/a'} le {formatDateTime(alert.acknowledgedAt)}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => handleAcknowledge(alert.id)}
                        disabled={acknowledgingId === alert.id || Boolean(alert.acknowledgedAt)}
                        sx={{ minHeight: 40, borderRadius: 99, borderColor: '#2f6b50', color: '#2f6b50' }}
                      >
                        {acknowledgingId === alert.id ? 'Acquittement...' : (alert.acknowledgedAt ? 'Acquittee' : 'Acquitter')}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => handleResolve(alert.id)}
                        disabled={resolvingId === alert.id}
                        sx={{ minHeight: 40, borderRadius: 99, borderColor: '#17323c', color: '#17323c' }}
                      >
                        {resolvingId === alert.id ? 'Resolution...' : 'Resoudre'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default MonitoringAdminPage;
